import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Remote, Rpc } from 'accounts'
import { Siwe } from 'ox'
import { useEffect, useRef, useState } from 'react'
import { Button, Frame } from 'regen-ui'
import { useChainId } from 'wagmi'
import * as z from 'zod/mini'

import { api } from '#/lib/api.js'
import * as Auth from '#/lib/auth.js'
import * as AuthorizedApps from '#/lib/authorized-apps.js'
import { getSupportedChainId } from '#/lib/chain.js'
import { wagmiConfig } from '#/lib/config.js'
import * as Email from '#/lib/email.js'
import * as Oidc from '#/lib/oidc.js'
import { useAnalytics } from '#/lib/telemetry/analytics.js'
import * as Frames from '../-frames/connect/index.js'
import { useDeposit } from '../-hooks/useDeposit.js'
import * as AuthorizeAccessKey from '../-lib/AuthorizeAccessKey.js'
import * as RemoteRpc from '../-lib/RemoteRpc.js'
import * as ShowDeposit from '../-lib/ShowDeposit.js'

export const Route = createFileRoute('/remote/rpc/wallet_connect')({
  component: Wrapper,
  validateSearch: (search) =>
    RemoteRpc.validateSearch(search, {
      method: 'wallet_connect',
      strict: true,
    }),
})

type AuthorizeAccessKey = NonNullable<z.output<typeof Rpc.wallet_connect.authorizeAccessKey>>

type ConnectResult = Rpc.wallet_connect.Encoded['returns']

/**
 * A completed `wallet_connect` whose response is deferred so the dialog can run
 * the post-connect identity and/or deposit prompts before sending it.
 */
type Pending = {
  /**
   * Email account label resolved during connect, used to pre-fill /
   * auto-send the verify step when the post-connect email prompt runs.
   */
  email: string | undefined
  /** Connect event (login vs. register) that drives deposit copy. */
  event: ShowDeposit.Event
  /** Deferred `wallet_connect` result waiting to be sent. */
  result: ConnectResult
  /** Resolved post-connect deposit capability, if any. */
  showDeposit: ShowDeposit.Requested | undefined
}

type Register = ReturnType<typeof useMutation<unknown, Error, SubmitVariables>>

type Search = Remote.validateSearch.ReturnType<'wallet_connect'>

type Submit = ReturnType<
  typeof useMutation<Pending | undefined, Error, SubmitVariables | undefined>
>

type SubmitVariables = {
  /** Existing accounts' credentials to target when signing in by verified email. */
  credentialId?: string | string[] | undefined
  method?: string | undefined
  name?: string | undefined
  selectAccount?: boolean | undefined
  /** Verified email to attach (and bind, for new accounts) after connect. */
  verifiedEmail?: VerifiedEmail | undefined
}

/** A verified email and binding proof from email-first account discovery. */
type VerifiedEmail = Email.checkOtp.ReturnType

/** Whether work after the ceremony must finish before the RPC result is sent. */
export function shouldDeferConnectResponse(options: {
  deposit: boolean
  identity: boolean
  verifiedEmail: boolean
}) {
  return options.verifiedEmail || options.identity || options.deposit
}

function Wrapper() {
  const search = Route.useSearch() as Search
  return <Component key={search.id} />
}

type Screen =
  /** Returning user with an existing passkey — prompt to sign in. */
  | { name: 'welcome-back' }
  /** Email + passkey entry form for new or returning users. */
  | { name: 'sign-in' }
  /** Authorize access-key scopes before answering the original connect request. */
  | { name: 'authorize'; variables: SubmitVariables }
  /** Verify an entered email before finding or creating its passkey. */
  | { name: 'verify-otp'; email: string }
  /** Verified email associated with multiple accounts; select one before WebAuthn. */
  | { name: 'select-account'; verified: VerifiedEmail }
  /** Optional verified-email identity step shown after connect, before responding. */
  | { name: 'verify-email'; pending: Pending }
  /** Optional deposit prompt shown after connect succeeds, before responding. */
  | {
      balanceGate: ShowDeposit.BalanceGate
      event: ShowDeposit.Event
      name: 'post-connect-deposit'
      result: ConnectResult
      showDeposit: ShowDeposit.Requested
    }

function Component() {
  const { capture } = useAnalytics()
  const search = Route.useSearch() as Search
  // Auth-capable connect flows require a webAuthn session; read-only mode must
  // behave as disconnected instead of rendering the returning-user path.
  const sessionAddress = Auth.useSessionAddress()
  const chainId = useChainId()
  const rpc = RemoteRpc.use()
  const origin = rpc.consumer.origin
  const consumer = RemoteRpc.useConsumer()
  const queryClient = useQueryClient()

  const capabilities = search._decoded.params?.[0]?.capabilities
  const method = capabilities?.method
  const originalAuthorizeAccessKey = capabilities?.authorizeAccessKey
  const personalSign = capabilities?.personalSign
  const showDeposit = capabilities?.showDeposit
  const identity = capabilities?.identity
  // The only identity claim today is a verified email, requested as `email:
  // true` (wallet reuses the SIWE nonce), `email: { nonce }` (explicit nonce
  // for standalone OIDC), or `email: 'x@y.z'` / `{ address }` (exact email the
  // user must authenticate with). Gate the in-flow prompt on any form.
  const identityEmail = identity?.email
  const identityRequested =
    identityEmail === true ||
    typeof identityEmail === 'string' ||
    (typeof identityEmail === 'object' && identityEmail !== null)
  // Explicit replay nonce baked into the id token; falls back to the SIWE
  // challenge nonce (parsed from the result) when the app uses `email: true`.
  const identityNonce =
    typeof identityEmail === 'object' && identityEmail ? identityEmail.nonce : undefined
  // Exact email required by the app: the sign-in entry is locked to it and the
  // user must authenticate with the account owning it. Values that aren't an
  // email are ignored (treated as a plain request).
  const identityAddress = (() => {
    const value =
      typeof identityEmail === 'string'
        ? identityEmail
        : typeof identityEmail === 'object' && identityEmail
          ? identityEmail.address
          : undefined
    const email = value?.trim().toLowerCase()
    return email && Email.isEmail(email) ? email : undefined
  })()
  // The wallet only signs the app-origin challenge. The app-side Provider
  // keeps `verify`/`logout`, then verifies the returned signature so the
  // session cookie lands on the app's origin.
  const auth = (() => {
    const value = capabilities?.auth
    if (!value || typeof value === 'string') return value
    const { challenge, returnToken } = value
    return {
      ...(challenge ? { challenge } : {}),
      ...(returnToken ? { returnToken } : {}),
    }
  })()
  const [authorizeAccessKey, setAuthorizeAccessKey] = useState(originalAuthorizeAccessKey)
  const [screen, setScreen] = useState<Screen>(() => {
    if (method === 'register') return { name: 'sign-in' }
    if (sessionAddress) return { name: 'welcome-back' }
    return { name: 'sign-in' }
  })

  AuthorizeAccessKey.useValidate({
    authorizeAccessKey,
    origin: rpc.consumer.origin,
    reject: rpc.reject,
    trusted: RemoteRpc.isTrusted(rpc),
  })
  // The deferred `wallet_connect` response must be sent exactly once, no matter
  // which post-connect prompt (identity, deposit) resolves it.
  const responded = useRef(false)
  const respondOnce = async (result: ConnectResult) => {
    if (responded.current) return
    responded.current = true
    await rpc.resolve(result)
  }

  // Attaches the verified email and a freshly-minted, app-bound id token to the
  // connect result. The token's nonce is the explicit request nonce, falling
  // back to the SIWE challenge nonce echoed in the result so apps using the
  // `auth` capability get replay protection without extra plumbing. Minting is
  // best-effort: the plain email is still shared if the token can't be issued.
  async function shareIdentity(result: ConnectResult, email: string) {
    const withEmail = withIdentityEmail(result, email)
    if (!origin) return withEmail
    const idToken = await Oidc.mintIdToken({
      audience: origin,
      nonce: identityNonce ?? siweNonce(result),
    }).catch(() => undefined)
    return idToken ? withIdentityIdToken(withEmail, idToken) : withEmail
  }

  // Runs after connect (and after any identity prompt): attaches the verified
  // email (when shared here), applies the optional deposit gate, then sends the
  // response once.
  const finalize = useMutation({
    mutationFn: async ({
      attach,
      pending,
      result: result_,
    }: {
      attach?: string | undefined
      pending: Pending
      result: ConnectResult
    }) => {
      const result = attach ? await shareIdentity(result_, attach) : result_
      if (pending.showDeposit) {
        const gate = await ShowDeposit.checkBalanceGate({
          address: result.accounts[0]?.address,
          chainId: getSupportedChainId(search._decoded.params?.[0]?.chainId ?? chainId),
          queryClient,
          showDeposit: pending.showDeposit,
        })
        if (gate.status === 'show')
          return {
            balanceGate: gate,
            event: pending.event,
            result,
            showDeposit: pending.showDeposit,
          }
      }
      await respondOnce(result)
      return undefined
    },
    onSuccess: (next) => {
      if (next) setScreen({ name: 'post-connect-deposit', ...next })
    },
  })

  const submit = useMutation({
    mutationFn: async (variables?: SubmitVariables): Promise<Pending | undefined> => {
      const capabilities = {
        ...(variables?.method ? { method: variables.method } : {}),
        ...(variables?.name ? { name: variables.name } : {}),
        ...(variables?.credentialId ? { credentialId: variables.credentialId } : {}),
        ...(variables?.selectAccount ? { selectAccount: true } : {}),
        ...(auth ? { auth } : {}),
        ...(authorizeAccessKey ? { authorizeAccessKey } : {}),
        ...(identity ? { identity } : {}),
        ...(personalSign ? { personalSign } : {}),
      }
      const params = [{ ...search.params?.[0], capabilities }] as const
      const request = {
        ...search,
        _decoded: { ...search._decoded, params },
        params,
      }
      const event = getShowDepositEvent(variables)
      const requestedShowDeposit = ShowDeposit.resolve(showDeposit, event)
      // Defer until verified-email validation/binding and any requested
      // identity or deposit step finish before sending the result.
      const defer = shouldDeferConnectResponse({
        deposit: !!requestedShowDeposit,
        identity: identityRequested,
        verifiedEmail: !!variables?.verifiedEmail,
      })
      // Let the remote RPC execute the provider request so the SDK's
      // sameOriginWithAncestors catch can trigger the popup fallback for
      // Bitwarden (and similar extensions) in cross-origin iframes.
      const result = (await RemoteRpc.execute(rpc, {
        defer,
        request: request as never,
      })) as ConnectResult | undefined
      // execute() returns undefined when the remote transport switches to popup mode.
      if (!result) return
      if (authorizeAccessKey) {
        const accessKeyAddress = result.accounts?.[0]?.capabilities?.keyAuthorization?.address
        // Prefer the chain explicitly requested by the dapp; fall back to the wallet's current chain.
        const chainId_ = authorizeAccessKey?.chainId ? Number(authorizeAccessKey.chainId) : chainId
        await AuthorizedApps.upsert({
          chainId: chainId_,
          key: { ...authorizeAccessKey, address: accessKeyAddress ?? authorizeAccessKey.address },
          hostname: consumer.host,
          title: AuthorizedApps.isCliHostname(consumer.host) ? consumer.name : undefined,
        })
      }
      // Resolve the verified-email identity claim here, inside the mutation, so
      // we never render a separate "loading your account" step: by the time a
      // frame shows we already know whether to attach an existing email or
      // prompt for one.
      const verifiedOtp = variables?.verifiedEmail
      const account = result.accounts[0]
      const claim = await (async () => {
        // The email was already verified before the ceremony. New accounts
        // bind it now that a session exists; only share it when requested.
        if (verifiedOtp) {
          if (!account?.address) throw new Error('Could not load your account.')
          if (verifiedOtp.accounts.length > 0) {
            // Signed in to an existing account — make sure it's associated
            // with the verified email before sharing it.
            if (
              !verifiedOtp.accounts.some(
                (match) => account.address.toLowerCase() === match.address.toLowerCase(),
              )
            )
              throw new Error('Signed in to a different account than expected.')
          } else {
            // New account: redeem the verification proof after the passkey
            // ceremony creates its authenticated session.
            await Email.bindEmail({ email: verifiedOtp.email, proof: verifiedOtp.proof })
          }
          return {
            email: undefined,
            result: identityRequested ? await shareIdentity(result, verifiedOtp.email) : result,
          }
        }
        // App requested an email but none is proven yet: look up the account so
        // we can attach an existing verified email directly, or prompt for one.
        // A failed lookup still prompts (the user can enter an email or skip)
        // rather than failing the connect, since the claim is optional per the RFC.
        if (identityRequested && account?.address) {
          const { address } = account
          // Fetch the profile directly (the wagmi-gated session may not yet
          // reflect the just-completed ceremony). Ignore failures / session drift.
          const me = await api.api.auth.me
            .$get()
            .then((res) => (res.ok ? res.json() : null))
            .then((data) =>
              data && data.address.toLowerCase() === address.toLowerCase() ? data : null,
            )
            .catch(() => null)
          if (me?.email) {
            // App pinned an exact email — never share a mismatched one. The
            // error returns the user to sign-in, locked to the required email.
            if (identityAddress && me.email.toLowerCase() !== identityAddress)
              throw new Error(`This app requires you to continue with ${identityAddress}.`)
            return { email: undefined, result: await shareIdentity(result, me.email) }
          }
          // No verified email yet → prompt, pinned to the required email or
          // pre-filling an email-looking label.
          const email =
            identityAddress ??
            (me?.username && Email.isEmail(me.username) ? me.username : undefined)
          return { email, result }
        }
        return { email: undefined, result }
      })()
      // Not deferred → respond() already sent the result; nothing left to do.
      if (!defer) return undefined
      return {
        email: claim.email,
        event,
        result: claim.result,
        showDeposit: requestedShowDeposit,
      }
    },
    onSuccess: (pending, variables) => {
      capture('auth_success', {
        type: variables?.method === 'register' ? 'signup' : 'signin',
      })
      if (!pending) return
      // Prompt for a verified email (before the deposit prompt) when the app
      // asked for one but the account doesn't have it yet — existing/proven
      // emails were already attached to the result in the mutation, so there's
      // no loading step here.
      const connected = pending.result.accounts[0]
      const needsEmail =
        identityRequested && !!connected?.address && !connected.capabilities?.identity?.email
      if (needsEmail) {
        setScreen({ name: 'verify-email', pending })
        return
      }
      finalize.mutate({ pending, result: pending.result })
    },
    onError: (error, variables) => {
      capture('auth_failure', {
        type: variables?.method === 'register' ? 'signup' : 'signin',
        error: error.message,
      })
    },
  })

  const register = useMutation({
    mutationFn: (variables: SubmitVariables) =>
      wagmiConfig.connectors[0]!.connect({
        capabilities: {
          ...(variables.method ? { method: variables.method } : {}),
          ...(variables.name ? { name: variables.name } : {}),
        },
      } as never),
    onSuccess: (_data, variables) => {
      if (authorizeAccessKey) setScreen({ name: 'authorize', variables })
      else capture('auth_success', { type: 'signup' })
    },
    onError: (error) => {
      capture('auth_failure', { type: 'signup', error: error.message })
    },
  })

  // Verify every email-shaped entry before any passkey ceremony so existing
  // accounts are discovered instead of silently creating another account.
  const emailDiscovery = useMutation({
    mutationFn: async (email: string) => {
      await Email.sendOtp(email)
      return email
    },
    onSuccess: (email) => setScreen({ name: 'verify-otp', email }),
  })

  const continueWithVerifiedEmail = (verified: VerifiedEmail, account = verified.accounts[0]) => {
    capture(account ? 'auth_passkey_login_started' : 'auth_signup_started')
    const variables = (
      account
        ? {
            method: 'login',
            credentialId: account.credentialId,
            verifiedEmail: verified,
          }
        : { method: 'register', name: verified.email, verifiedEmail: verified }
    ) satisfies SubmitVariables
    if (authorizeAccessKey) setScreen({ name: 'authorize', variables })
    else submit.mutate(variables)
  }

  if (screen.name === 'verify-otp')
    return (
      <VerifyOtp
        email={screen.email}
        onBack={() => setScreen({ name: 'sign-in' })}
        onVerified={(verified) => {
          if (verified.accounts.length > 0) {
            setScreen({ name: 'select-account', verified })
            // Start a sole account immediately, but retain the chooser so a
            // cancelled or failed passkey ceremony can be retried directly.
            if (verified.accounts.length === 1) continueWithVerifiedEmail(verified)
            return
          }
          continueWithVerifiedEmail(verified)
        }}
        submitError={submit.error?.message}
        submitting={submit.isPending}
      />
    )

  if (screen.name === 'select-account')
    return (
      <Frames.SelectAccount
        accounts={screen.verified.accounts}
        email={screen.verified.email}
        error={submit.error?.message}
        loading={submit.isPending}
        onBack={identityAddress ? undefined : () => setScreen({ name: 'sign-in' })}
        onSelect={(account) => continueWithVerifiedEmail(screen.verified, account)}
        selectedCredentialId={
          typeof submit.variables?.credentialId === 'string'
            ? submit.variables.credentialId
            : undefined
        }
      />
    )

  if (screen.name === 'verify-email')
    return (
      <VerifyEmail
        locked={!!identityAddress}
        onShare={(email) =>
          finalize.mutate({
            attach: email,
            pending: screen.pending,
            result: screen.pending.result,
          })
        }
        // The app required a verified email; declining it rejects the connect.
        onSkip={() => rpc.reject()}
        pending={screen.pending}
        sharing={finalize.isPending}
      />
    )

  if (screen.name === 'post-connect-deposit')
    return (
      <PostConnectDeposit
        balanceGate={screen.balanceGate}
        event={screen.event}
        onContinue={() => {
          void respondOnce(screen.result)
        }}
        result={screen.result}
        showDeposit={screen.showDeposit}
      />
    )

  if (screen.name === 'welcome-back')
    return (
      <WelcomeBack
        authorizeAccessKey={authorizeAccessKey}
        expiry_original={originalAuthorizeAccessKey?.expiry}
        message={personalSign?.message}
        onAuthorizeAccessKeyChange={setAuthorizeAccessKey}
        onSignUp={() => setScreen({ name: 'sign-in' })}
        requiredEmail={identityAddress}
        submit={submit}
      />
    )
  if (screen.name === 'authorize')
    return (
      <Authorize
        authorizeAccessKey={authorizeAccessKey!}
        expiry_original={originalAuthorizeAccessKey?.expiry}
        message={personalSign?.message}
        onAuthorizeAccessKeyChange={setAuthorizeAccessKey}
        submit={submit}
        variables={screen.variables}
      />
    )
  return (
    <SignIn
      emailDiscovery={emailDiscovery}
      email={identityAddress}
      message={personalSign?.message}
      onAuthorize={
        authorizeAccessKey
          ? (variables) => {
              if (variables.method === 'register') register.mutate(variables)
              else
                setScreen({
                  name: 'authorize',
                  variables,
                })
            }
          : undefined
      }
      register={register}
      submit={submit}
    />
  )
}

/** Wires email verification to account discovery. */
function VerifyOtp(props: VerifyOtp.Props) {
  const { email, onBack, onVerified, submitError, submitting } = props
  const { code, error, onChange, resend, resendStatus, verifying } = Email.useVerify({
    email,
    onSuccess: onVerified,
    verify: Email.checkOtp,
  })

  return (
    <Frames.VerifyOtp
      code={code}
      email={email}
      error={error ?? submitError}
      onBack={onBack}
      onChange={onChange}
      onResend={resend}
      resendStatus={resendStatus}
      verifying={verifying || submitting}
    />
  )
}

declare namespace VerifyOtp {
  type Props = {
    /** Email being verified. */
    email: string
    /** Called to return to the sign-in screen and edit the email. */
    onBack: () => void
    /** Called with the verified email to run the ceremony. */
    onVerified: (verified: VerifiedEmail) => void
    /** Error from the connect ceremony triggered by the verified email, if any. */
    submitError?: string | undefined
    /** Whether the connect ceremony triggered by the verified email is in flight. */
    submitting: boolean
  }
}

/** Wires WelcomeBack screen to real data (origin, address, /auth/me). */
function WelcomeBack(props: WelcomeBack.Props) {
  const {
    authorizeAccessKey,
    expiry_original,
    message,
    onAuthorizeAccessKeyChange,
    onSignUp,
    requiredEmail,
    submit,
  } = props
  const address = Auth.useSessionAddress()
  const consumer = RemoteRpc.useConsumer()
  const host = consumer.name ?? consumer.host
  const me = Auth.me.useQuery()
  const label =
    me.data?.email ??
    me.data?.username ??
    (address ? `${address.slice(0, 8)}…${address.slice(-6)}` : undefined)

  // App pinned an exact email — a session on a different verified email can't
  // satisfy it. Divert to sign-in, locked to the required email.
  const mismatch =
    !!requiredEmail && !!me.data?.email && me.data.email.toLowerCase() !== requiredEmail
  useEffect(() => {
    if (mismatch) onSignUp()
  }, [mismatch, onSignUp])

  return (
    <Frames.WelcomeBack
      address={address}
      authorizeAccessKey={authorizeAccessKey}
      error={submit.error?.message}
      expiry_original={expiry_original}
      host={host}
      label={label}
      loading={submit.isPending && !submit.variables?.selectAccount}
      message={message}
      onContinue={() => submit.mutate({})}
      onAuthorizeAccessKeyChange={onAuthorizeAccessKeyChange}
      onCreateNew={onSignUp}
      onSignIn={() => submit.mutate({ selectAccount: true })}
    />
  )
}

declare namespace WelcomeBack {
  type Props = {
    /** Access key authorization params. */
    authorizeAccessKey?: AuthorizeAccessKey | undefined
    /** Original requested expiry, used to keep edits from broadening permissions. */
    expiry_original?: number | undefined
    /** Called with edited access key params. */
    onAuthorizeAccessKeyChange?: ((authorizeAccessKey: AuthorizeAccessKey) => void) | undefined
    /** Message that will be signed during connect. */
    message?: string | undefined
    /** Called when the user clicks "Create a new account". */
    onSignUp: () => void
    /** Exact email required by the app — mismatched sessions divert to sign-in. */
    requiredEmail?: string | undefined
    /** Mutation handle for submitting the request. */
    submit: Submit
  }
}

/** Routes email-shaped entries through discovery while preserving labels for registration. */
export function getSignInSubmission(label: string) {
  if (Email.isEmail(label)) return { email: label.trim().toLowerCase(), type: 'discover' } as const
  return { name: label, type: 'register' } as const
}

/** Wires SignIn screen to passkey registration/login. */
function SignIn(props: SignIn.Props) {
  const { capture } = useAnalytics()
  const { email, emailDiscovery, message, onAuthorize, register, submit } = props
  const consumer = RemoteRpc.useConsumer()
  const host = consumer.name ?? consumer.host

  return (
    <Frames.SignIn
      email={email}
      error={emailDiscovery.error?.message ?? register.error?.message ?? submit.error?.message}
      host={host}
      message={message}
      onPasskey={() => {
        capture('auth_passkey_login_started')
        const variables = { method: 'login', selectAccount: true } satisfies SubmitVariables
        if (onAuthorize) onAuthorize(variables)
        else submit.mutate(variables)
      }}
      onSubmit={(label) => {
        const submission = getSignInSubmission(label)
        if (submission.type === 'discover') {
          emailDiscovery.mutate(submission.email)
          return
        }
        capture('auth_signup_started')
        const variables = {
          method: 'register',
          name: submission.name,
        } satisfies SubmitVariables
        if (onAuthorize) onAuthorize(variables)
        else submit.mutate(variables)
      }}
      passkeyLoading={submit.isPending && submit.variables?.method === 'login'}
      registerLoading={
        emailDiscovery.isPending ||
        register.isPending ||
        (submit.isPending && submit.variables?.method === 'register')
      }
    />
  )
}

declare namespace SignIn {
  type Props = {
    /** Exact email required by the app — pre-filled and locked in the frame. */
    email?: string | undefined
    /** Send-OTP mutation that discovers accounts before registering an email. */
    emailDiscovery: ReturnType<typeof useMutation<string, Error, string>>
    /** Message that will be signed during connect. */
    message?: string | undefined
    /** Intercepts sign-in/register to show authorize screen. Receives `{ method: 'login' | 'register', ... }`. */
    onAuthorize?: ((variables: SubmitVariables) => void) | undefined
    /** Register mutation for the 2-step create + authorize flow. */
    register: Register
    /** Mutation handle for submitting the request. */
    submit: Submit
  }
}

/** Authorize screen — confirms scopes before completing the pending connect flow. */
function Authorize(props: Authorize.Props) {
  const {
    authorizeAccessKey,
    expiry_original,
    message,
    onAuthorizeAccessKeyChange,
    submit,
    variables,
  } = props
  const rpc = RemoteRpc.use()
  const consumer = RemoteRpc.useConsumer()
  const host = consumer.name ?? consumer.host
  const mutation = submit

  return (
    <Frames.SignInAuthorize
      authorizeAccessKey={authorizeAccessKey}
      confirming={mutation.isPending}
      error={mutation.error?.message}
      expiry_original={expiry_original}
      host={host}
      message={message}
      onApprove={() => {
        // Answer `wallet_connect` after approval so auth/personalSign capabilities still run.
        submit.mutate(variables)
      }}
      onAuthorizeAccessKeyChange={onAuthorizeAccessKeyChange}
      onReject={() => rpc.reject()}
    />
  )
}

declare namespace Authorize {
  type Props = {
    /** Access key authorization params. */
    authorizeAccessKey: AuthorizeAccessKey
    /** Original requested expiry, used to keep edits from broadening permissions. */
    expiry_original?: number | undefined
    /** Called with edited access key params. */
    onAuthorizeAccessKeyChange: (authorizeAccessKey: AuthorizeAccessKey) => void
    /** Message that will be signed during connect. */
    message?: string | undefined
    /** Mutation handle for the approved connect flow. */
    submit: Submit
    /** Variables for the approved connect flow. */
    variables: SubmitVariables
  }
}

function PostConnectDeposit(props: PostConnectDeposit.Props) {
  const { balanceGate, event, onContinue, result, showDeposit } = props
  const search = Route.useSearch() as Search
  const chainId = useChainId()
  const consumer = RemoteRpc.useConsumer()
  const host = consumer.name ?? consumer.host
  const params = typeof showDeposit === 'object' ? showDeposit : undefined
  const address = result.accounts[0]?.address
  const chainId_resolved = getSupportedChainId(search._decoded.params?.[0]?.chainId ?? chainId)
  const deposit = useDeposit({
    address,
    amount: params?.amount,
    chainId: chainId_resolved,
    onSuccess: onContinue,
    orActions: (
      <Button
        className="w-full"
        onClick={onContinue}
        size="medium"
        type="button"
        variant="secondary"
      >
        Skip and continue
      </Button>
    ),
    successScreen: false,
    token: params?.token,
  })

  if (deposit.frame) return <Frame.Provider onDismiss={onContinue}>{deposit.frame}</Frame.Provider>

  return (
    <Frame
      bodyClassName="gap-2"
      onDismiss={onContinue}
      subtitle={ShowDeposit.getSubtitle({
        amount: params?.amount,
        balanceGate,
        event,
        host,
        token: params?.token,
      })}
      title="Fund your account"
    >
      {deposit.actions}
    </Frame>
  )
}

declare namespace PostConnectDeposit {
  type Props = {
    /** Balance-gate result that decided to show this prompt. */
    balanceGate: ShowDeposit.BalanceGate
    /** Connect event that triggered the optional deposit prompt. */
    event: ShowDeposit.Event
    /** Sends the saved `wallet_connect` result to the requesting app. */
    onContinue: () => void
    /** Completed `wallet_connect` result waiting on the optional deposit prompt. */
    result: ConnectResult
    /** Requested post-connect deposit capability. */
    showDeposit: ShowDeposit.Requested
  }
}

/** Wires the verified-email identity prompt to origin/account context. */
function VerifyEmail(props: VerifyEmail.Props) {
  const { locked, onShare, onSkip, pending, sharing } = props
  const consumer = RemoteRpc.useConsumer()
  const host = consumer.name ?? consumer.host
  return (
    <Frames.VerifyEmail
      email={pending.email}
      host={host}
      locked={locked}
      onShare={onShare}
      onSkip={onSkip}
      sharing={sharing}
    />
  )
}

declare namespace VerifyEmail {
  type Props = {
    /** Whether the email is required by the app and can't be edited. */
    locked?: boolean | undefined
    /** Called with the verified email to share. */
    onShare: (email: string) => void
    /** Called to continue the connect without sharing an email. */
    onSkip: () => void
    /** Deferred connect awaiting the identity decision. */
    pending: Pending
    /** Whether the response is being finalized. */
    sharing: boolean
  }
}

function getShowDepositEvent(variables: SubmitVariables | undefined): ShowDeposit.Event {
  if (variables?.method === 'register') return 'register'
  return 'login'
}

/**
 * Returns a copy of a `wallet_connect` result with the verified email attached
 * to the authenticated account's `capabilities.identity`. Per the RFC,
 * `identity` is omitted unless an email is shared, so this is only called once a
 * verified email is available. The claim is attached to the first
 * (authenticated) account only.
 */
export function withIdentityEmail(result: ConnectResult, email: string): ConnectResult {
  return {
    ...result,
    accounts: result.accounts.map((account, index) =>
      index === 0
        ? {
            ...account,
            capabilities: {
              ...account.capabilities,
              identity: { ...account.capabilities?.identity, email },
            },
          }
        : account,
    ),
  }
}

/**
 * Returns a copy of a `wallet_connect` result with a signed identity token (JWT)
 * attached to the authenticated account's `capabilities.identity`. Sibling to
 * {@link withIdentityEmail}: the email is the client-side string, the id token
 * is its server-verifiable counterpart. Attached to the first account only.
 */
export function withIdentityIdToken(result: ConnectResult, idToken: string): ConnectResult {
  return {
    ...result,
    accounts: result.accounts.map((account, index) =>
      index === 0
        ? {
            ...account,
            capabilities: {
              ...account.capabilities,
              identity: { ...account.capabilities?.identity, idToken },
            },
          }
        : account,
    ),
  }
}

/**
 * Extracts the SIWE `nonce` from the signed message echoed on the connect
 * result (`auth` capability). Used to bind the id token to the same single-use
 * nonce the app already issued for its SIWE challenge, so no separate nonce
 * round-trip is needed. Returns `undefined` when no SIWE message is present.
 */
function siweNonce(result: ConnectResult): string | undefined {
  const message = result.accounts[0]?.capabilities?.personalSign?.message
  if (!message) return undefined
  try {
    return Siwe.parseMessage(message).nonce
  } catch {
    return undefined
  }
}
